import React, { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const MIN_PER_DELIVERY = 2;

// First delivery: today + remainder of bottles.
// Every subsequent delivery gets an equal base amount.
function buildSplitCounts(totalQty, deliveriesCount) {
  if (!totalQty || !deliveriesCount || deliveriesCount < 1) return [];
  const base = Math.floor(totalQty / deliveriesCount);
  const remainder = totalQty - base * deliveriesCount;
  const counts = Array(deliveriesCount).fill(base);
  counts[0] += remainder;
  return counts;
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function isFriday(date) {
  return date.getDay() === 5; // 0=Sun ... 5=Fri ... 6=Sat
}

function isToday(date) {
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
}

export default function PartialDeliveryDialog({
  isOpen,
  onClose,
  totalQty,
  unitPrice, // already-discounted price per bottle for this order's tier
  discountLabel, // e.g. "40% OFF" for display only
  existingSchedule, // remembered schedule from last time dialog was open
  onConfirm,
}) {
  const maxDeliveries = Math.max(1, Math.floor(totalQty / MIN_PER_DELIVERY));

  const [splitMode, setSplitMode] = useState("deliveries"); // 'deliveries' | 'perDelivery'
  const [deliveriesCount, setDeliveriesCount] = useState(
    existingSchedule?.deliveries?.length || 2
  );
  const [perDeliveryCount, setPerDeliveryCount] = useState(
    existingSchedule?.deliveries?.[1]?.quantity || MIN_PER_DELIVERY
  );
  const [firstDeliveryDate, setFirstDeliveryDate] = useState(
    existingSchedule?.deliveries?.[0]?.date
      ? new Date(existingSchedule.deliveries[0].date)
      : null
  );
  const [error, setError] = useState("");

  // Keep deliveriesCount / perDeliveryCount in sync when mode changes,
  // so switching modes doesn't produce a jarring jump.
  useEffect(() => {
    if (splitMode === "perDelivery") {
      const derived = Math.max(
        MIN_PER_DELIVERY,
        Math.floor(totalQty / deliveriesCount)
      );
      setPerDeliveryCount(derived);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [splitMode]);

  // When user edits perDeliveryCount, derive an equivalent deliveriesCount.
  const handlePerDeliveryChange = (val) => {
    const num = Math.max(MIN_PER_DELIVERY, parseInt(val) || MIN_PER_DELIVERY);
    setPerDeliveryCount(num);
    const derivedDeliveries = Math.max(1, Math.ceil(totalQty / num));
    setDeliveriesCount(derivedDeliveries);
  };

  const handleDeliveriesChange = (val) => {
    const num = Math.max(2, parseInt(val) || 2);
    setDeliveriesCount(num);
  };

  const counts = useMemo(
    () => buildSplitCounts(totalQty, deliveriesCount),
    [totalQty, deliveriesCount]
  );

  useEffect(() => {
    if (deliveriesCount > maxDeliveries) {
      setError(
        `Each delivery needs at least ${MIN_PER_DELIVERY} bottles — max ${maxDeliveries} deliveries for ${totalQty} bottles.`
      );
    } else if (counts.length && counts[counts.length - 1] < MIN_PER_DELIVERY) {
      setError(
        `That split leaves fewer than ${MIN_PER_DELIVERY} bottles in a delivery. Reduce the number of deliveries.`
      );
    } else {
      setError("");
    }
  }, [deliveriesCount, counts, maxDeliveries, totalQty]);

  const schedule = useMemo(() => {
    if (!firstDeliveryDate || error) return [];
    return counts.map((qty, idx) => {
      const date = addDays(firstDeliveryDate, idx * 7);
      const subtotal = qty * unitPrice;
      const vat = parseFloat((subtotal * 0.05).toFixed(2));
      return {
        deliveryNumber: idx + 1,
        date: date.toISOString(),
        quantity: qty,
        unitPrice,
        subtotal: parseFloat(subtotal.toFixed(2)),
        vat,
        total: parseFloat((subtotal + vat).toFixed(2)),
      };
    });
  }, [counts, firstDeliveryDate, unitPrice, error]);

  const handleConfirm = () => {
    if (!firstDeliveryDate) {
      setError("Please select a first delivery date.");
      return;
    }
    if (error) return;

    onConfirm({
      totalQty,
      unitPrice,
      deliveries: schedule,
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-primaryVeryLight rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center mb-4">
          <div className="bg-blue-100 rounded-full p-3 mr-4">
            {/* ICON SLOT: e.g. calendar/truck icon */}
            <span className="w-6 h-6 inline-block" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              Split Into Partial Deliveries
            </h3>
            {discountLabel && (
              <p className="text-xs text-green-700 font-medium">
                Locked-in price: {discountLabel}
              </p>
            )}
          </div>
        </div>

        <p className="text-sm text-gray-700 mb-4">
          You have <strong>{totalQty} x 5-gallon bottles</strong>. Choose how to
          split delivery — each delivery needs at least {MIN_PER_DELIVERY}{" "}
          bottles, one week apart.
        </p>

        {/* Split mode toggle */}
        <div className="flex gap-3 mb-4">
          <button
            type="button"
            onClick={() => setSplitMode("deliveries")}
            className={`flex-1 px-3 py-2 rounded-md text-sm font-medium border transition ${
              splitMode === "deliveries"
                ? "bg-primary text-textWhite border-primary"
                : "border-gray-300 text-gray-700 hover:bg-gray-50"
            }`}
          >
            Number of deliveries
          </button>
          <button
            type="button"
            onClick={() => setSplitMode("perDelivery")}
            className={`flex-1 px-3 py-2 rounded-md text-sm font-medium border transition ${
              splitMode === "perDelivery"
                ? "bg-primary text-textWhite border-primary"
                : "border-gray-300 text-gray-700 hover:bg-gray-50"
            }`}
          >
            Bottles per delivery
          </button>
        </div>

        {splitMode === "deliveries" ? (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Number of deliveries (max {maxDeliveries})
            </label>
            <input
              type="number"
              min={2}
              max={maxDeliveries}
              value={deliveriesCount}
              onChange={(e) => handleDeliveriesChange(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>
        ) : (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bottles per delivery (min {MIN_PER_DELIVERY})
            </label>
            <input
              type="number"
              min={MIN_PER_DELIVERY}
              max={totalQty}
              value={perDeliveryCount}
              onChange={(e) => handlePerDeliveryChange(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>
        )}

        {/* First delivery date */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            First delivery date
          </label>
          <DatePicker
            selected={firstDeliveryDate}
            onChange={(date) => setFirstDeliveryDate(date)}
            minDate={addDays(new Date(), 1)}
            filterDate={(date) => !isFriday(date) && !isToday(date)}
            placeholderText="Select a date"
            className="w-full border border-gray-300 rounded-md px-3 py-2"
            dateFormat="dd MMM yyyy"
          />
          <p className="text-xs text-gray-500 mt-1">
            Fridays and today are unavailable. Later deliveries are set
            automatically, 7 days apart.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-3 text-sm text-red-700 mb-4">
            {error}
          </div>
        )}

        {/* Schedule preview */}
        {schedule.length > 0 && (
          <div className="bg-white border-2 border-green-200 rounded-lg p-4 mb-4">
            <h4 className="font-semibold text-gray-900 mb-3 text-center">
              Delivery Schedule
            </h4>
            <div className="space-y-2 text-sm">
              {schedule.map((d) => (
                <div
                  key={d.deliveryNumber}
                  className="flex justify-between items-center py-2 border-b border-gray-200 last:border-b-0"
                >
                  <div>
                    <div className="font-medium text-gray-900">
                      Delivery {d.deliveryNumber}
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(d.date).toLocaleDateString(undefined, {
                        weekday: "short",
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}{" "}
                      · {d.quantity} bottles
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-gray-900">
                      {d.total.toFixed(2)} AED
                    </div>
                    <div className="text-xs text-gray-500">
                      ({d.subtotal.toFixed(2)} + {d.vat.toFixed(2)} VAT)
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 hover:bg-darkVeryLight rounded-md text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition"
          >
            {/* ICON SLOT */}
            <span className="w-4 h-4 inline-block" />
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!!error || !firstDeliveryDate}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-transparent bg-primary hover:bg-primaryHover text-base font-medium hover:text-textBlack text-textWhite shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {/* ICON SLOT */}
            <span className="w-4 h-4 inline-block" />
            Confirm Schedule
          </button>
        </div>
      </div>
    </div>
  );
}
